/**
 * FilterButton.tsx - Filter Toggle Button with Badge
 *
 * Displays a button to open the filter dropdown/modal.
 * Shows a badge with the count of active filters.
 * Supports RTL layouts and responsive sizing.
 */

import React from 'react';
import { IconButton, Badge, Box } from '@chakra-ui/react';
import { TooltipRoot, TooltipTrigger, TooltipContent, TooltipPositioner, Portal } from '@chakra-ui/react';
import { MdFilterList } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useFilterContext } from '@/contexts/FilterContext';

interface FilterButtonProps {
    disabled?: boolean;
    onClick: () => void;
}

export const FilterButton: React.FC<FilterButtonProps> = ({ disabled = false, onClick }) => {
    const { t } = useTranslation();
    const { getActiveFilterCount, hasActiveFilters } = useFilterContext();

    const filterCount = getActiveFilterCount();
    const isActive = hasActiveFilters();

    return (
        <TooltipRoot openDelay={500} positioning={{ placement: 'top' }}>
            <TooltipTrigger asChild>
                <Box position="relative">
                    <IconButton
                        aria-label={t('filters') || 'Filters'}
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        onClick={onClick}
                        width={{ base: '32px', md: '40px' }}
                        height={{ base: '32px', md: '40px' }}
                        borderRadius="8px"
                        background={isActive ? 'rgba(51, 51, 51, 0.05)' : 'transparent'}
                        color="#333333"
                        cursor="pointer"
                        _hover={{ background: isActive ? "var(--cair-accent-hover)" : "var(--cair-accent-subtle)" }}
                    >
                        <MdFilterList />
                    </IconButton>
                    {isActive && filterCount > 0 && (
                        <Badge
                            position="absolute"
                            top="-4px"
                            right="-4px"
                            borderRadius="full"
                            fontSize="10px"
                            minWidth="18px"
                            height="18px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            background="#333333"
                            color="white"
                            fontWeight="600"
                            paddingX="4px"
                            zIndex={1}
                        >
                            {filterCount > 99 ? '99+' : filterCount}
                        </Badge>
                    )}
                </Box>
            </TooltipTrigger>
            <Portal>
                <TooltipPositioner>
                    <TooltipContent>{t('filters') || 'Filters'}</TooltipContent>
                </TooltipPositioner>
            </Portal>
        </TooltipRoot>
    );
};

